import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import { ShieldCheck, FileBadge, ClipboardCheck, Bell, Check } from 'lucide-react';
import { DaysBadge, fmtDate } from './complianceShared';

const KPI = ({ label, value, tone, testid }) => {
  const tones = {
    default: 'text-[#0F172A]',
    amber: 'text-amber-600',
    rose: 'text-rose-600',
    indigo: 'text-indigo-600',
    slate: 'text-slate-500',
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={testid}>
      <div className="kpi-overline mb-2">{label}</div>
      <div className={`font-display text-2xl font-extrabold ${tones[tone || 'default']}`}>{value ?? '—'}</div>
    </div>
  );
};

export default function ComplianceDashboard() {
  const [summary, setSummary] = useState(null);
  const [reminders, setReminders] = useState([]);

  const load = async () => {
    try {
      const [s, r] = await Promise.all([
        apiGet('/compliance/dashboard'),
        apiGet('/compliance/reminders'),
      ]);
      setSummary(s);
      setReminders(r.items || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const acknowledge = async (id) => {
    try {
      await apiPost(`/compliance/reminders/${id}/acknowledge`);
      toast.success('Reminder acknowledged');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to acknowledge'); }
  };

  return (
    <div data-testid="compliance-dashboard">
      <div className="mb-6">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">People &amp; HR · Compliance</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Compliance Tracker</h1>
        <p className="text-sm text-slate-600 mt-1">Insurance policies · Statutory registrations · Renewal reminders across all projects.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <KPI label="Total Active Policies" value={summary?.total_active_policies} testid="kpi-active-policies" />
        <KPI label="Expiring within 30 days" value={summary?.expiring_30} tone="amber" testid="kpi-expiring-30" />
        <KPI label="Overdue / Expired" value={summary?.overdue} tone="rose" testid="kpi-overdue" />
        <KPI label="Statutory Required" value={summary?.statutory_required} tone="indigo" testid="kpi-statutory-required" />
        <KPI label="Pending Enforcement" value={summary?.pending_enforcement} tone="slate" testid="kpi-pending-enforcement" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <Bell size={14} className="text-[#3b4872]" />
            <span className="font-display text-sm font-bold">Renewal Reminders</span>
            <span className="text-xs text-slate-400">({reminders.length})</span>
          </div>
          <div className="max-h-[320px] overflow-y-auto divide-y divide-slate-100" data-testid="compliance-reminders-panel">
            {reminders.length === 0 && (
              <div className="p-6 text-sm text-slate-400 text-center">No upcoming renewals. All compliance records are within safe windows.</div>
            )}
            {reminders.map(r => (
              <div key={r.id} className="px-4 py-3 flex items-center gap-3" data-testid="compliance-reminder-card">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{r.policy_type_label}</div>
                  <div className="text-xs text-slate-500">
                    {r.project_name ? `${r.project_name} · ` : ''}Expires {fmtDate(r.expiry_date)}
                  </div>
                </div>
                <DaysBadge days={r.days_remaining} />
                {r.is_acknowledged ? (
                  <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-1"><Check size={12} /> Acked</span>
                ) : (
                  <button onClick={() => acknowledge(r.id)} data-testid="acknowledge-reminder-btn"
                          className="text-[10px] font-bold border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded-sm">
                    Acknowledge
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <Link to="/app/hr/compliance/insurance" data-testid="link-insurance-tab"
                className="block bg-white border border-slate-200 rounded-sm p-5 hover:border-[#3b4872] transition-colors group">
            <FileBadge size={20} className="text-[#3b4872] mb-3" />
            <div className="font-display font-bold text-slate-800 group-hover:text-[#3b4872]">Insurance</div>
            <p className="text-xs text-slate-500 mt-1">{summary?.total_policies ?? 0} policies across 10 insurance types — CAR, WC, PI, Medical, GPA &amp; more.</p>
          </Link>
          <Link to="/app/hr/compliance/statutory" data-testid="link-statutory-tab"
                className="block bg-white border border-slate-200 rounded-sm p-5 hover:border-[#3b4872] transition-colors group">
            <ClipboardCheck size={20} className="text-[#3b4872] mb-3" />
            <div className="font-display font-bold text-slate-800 group-hover:text-[#3b4872]">Statutory</div>
            <p className="text-xs text-slate-500 mt-1">{summary?.total_statutory ?? 0} registrations — Labour License, BOCW, Pollution Certificates.</p>
          </Link>
          <Link to="/app/hr/compliance/vault" data-testid="link-vault"
                className="block bg-white border border-slate-200 rounded-sm p-5 hover:border-[#3b4872] transition-colors group">
            <ShieldCheck size={20} className="text-[#3b4872] mb-3" />
            <div className="font-display font-bold text-slate-800 group-hover:text-[#3b4872]">Compliance Vault</div>
            <p className="text-xs text-slate-500 mt-1">All uploaded policies &amp; certificates, filterable by project.</p>
          </Link>
        </div>
      </div>

      {(summary?.by_project || []).length > 0 && (
        <div className="mt-6 bg-white border border-slate-200 rounded-sm">
          <div className="px-4 py-3 border-b border-slate-200 font-display text-sm font-bold">Compliance by Project</div>
          <table className="w-full text-sm" data-testid="compliance-by-project-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="px-4 py-2">Project</th>
                <th className="px-4 py-2 text-right">Insurance Policies</th>
                <th className="px-4 py-2 text-right">Statutory Records</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_project.map(p => (
                <tr key={p.project_id} className="border-b border-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-700">{p.project_name || p.project_id}</td>
                  <td className="px-4 py-2 text-right">{p.policies}</td>
                  <td className="px-4 py-2 text-right">{p.statutory}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
